import { Alert, Badge, Box, Button, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';
import { Observable } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';

import { If } from 'app/components/if';
import { RequestStatus } from 'app/components/request-status';
import { RequireRole } from 'app/components/require-role';
import { SeamRequestStats } from 'app/components/seam-request-stats';
import { SingleEditText } from 'app/components/single-edit-text';
import { TabPanel } from 'app/components/tab-panel';
import { Translate } from 'app/components/translate';
import { Role } from 'app/constants/role';
import { SeamStatus } from 'app/constants/seam-status';
import { SeamTransitions } from 'app/constants/seam-transitions';
import { Comment } from 'app/model/comment';
import { CrisRequest } from 'app/model/cris-request';
import { RestfulModel } from 'app/model/restful-model';
import { SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { messagingService } from 'app/services';
import { dialogService } from 'app/services/dialog.service';
import { seamRequestService } from 'app/services/seam-request.service';
import { RootState } from 'app/store';
import { Formatters } from 'app/utils/formatters';
import { CrisSummary } from 'features/cris/request/cris-summary';

import { SeamSummary } from './seam-summary';
import { selectRoles } from '../../../auth/auth.slice';
import { Comments } from '../../../comments/comments';
import { addCrisComment, loadCrisComments, selectCrisComments, setCrisComments } from '../../../cris/cris.slice';
import { addSeamComment, loadSeamComments, selectRequest, selectSeamComments, setSeamComments } from '../../slice';

interface DetailsProps {
    request: SeamRequest;
    /**
     * Patterns in the current seam request.
     */
    patterns?: SeamPattern[];

    // Redux Props
    seamComments: Comment[];
    crisComments: Comment[];
    roles: Record<string, boolean>;

    loadSeamComments: (request: RestfulModel) => Observable<Comment[]>;

    loadCrisComments: (request: RestfulModel) => Observable<Comment[]>;

    setSeamComments: (comments: Comment[]) => void;

    setCrisComments: (comments: Comment[]) => void;

    addSeamComment: (comment: string, request: SeamRequest) => Observable<Comment>;

    addCrisComment: (comment: string, request: CrisRequest) => Observable<Comment>;

    selectRequest: (request: SeamRequest) => void;
}

interface DetailState {
    tabValue: number;
    open: boolean;
}

class Details extends React.Component<DetailsProps, DetailState> {
    public constructor(props: DetailsProps | Readonly<DetailsProps>) {
        super(props);

        this.state = {
            tabValue: 0,
            open: false,
        };
    }

    public handleClickOpen = () => {
        dialogService.showConfirm(
            {
                cancelText: 'Cancel',
                confirmText: 'Yes',
                message: 'Are you sure you want to revert the publish in Production?',
            },
            () => this.transition(SeamTransitions.UNPUBLISH),
        );
    };
    public handleClickPublish = () => {
        dialogService.showConfirm(
            {
                cancelText: 'Cancel',
                confirmText: 'Yes',
                message: 'Are you sure you want to Approve and Deploy this request to Production?',
            },
            () => this.transition(SeamTransitions.QA_APPROVED),
        );
    };
    public handleClickProdQAApproved = () => {
        dialogService.showConfirm(
            {
                cancelText: 'Cancel',
                confirmText: 'Yes',
                message: 'Are you sure you want to Approve ?',
            },
            () => this.transition(SeamTransitions.PROD_QA_APPROVED),
        );
    };

    public componentDidMount() {
        this.props.loadSeamComments(this.props.request).subscribe((value) => {
            this.props.setSeamComments(value);
        });

        this.props.loadCrisComments(this.props.request.crisRequest).subscribe((value) => {
            this.props.setCrisComments(value);
        });
    }

    public render() {
        const request = this.props.request;

        return (
            <Box flexDirection='column'>
                <Box display='flex'>
                    <Box flexGrow={1}>
                        <Typography variant='h4'>Issue Details</Typography>
                    </Box>
                    <Box flexGrow={0}>
                        {this.getActionButton()}
                        {this.getCloseButton()}
                    </Box>
                </Box>

                <RequireRole roles={Role.QA_MANAGER}>
                    <If condition={request.currentPlace === SeamStatus.QA}>
                        {request.store.inProd ? (
                            <Alert severity='success' className='my-1'>
                                Store is in production
                            </Alert>
                        ) : (
                            <Alert severity='warning' className='my-1'>
                                Store is not in production
                            </Alert>
                        )}
                    </If>
                </RequireRole>

                <Tabs
                    value={this.state.tabValue}
                    onChange={(evt, newVal) => this.setState({ tabValue: newVal as number })}
                    textColor='primary'
                    indicatorColor='primary'
                >
                    <Tab
                        label={
                            <span>
                                Seam Task <RequestStatus status={request.currentPlace} />
                            </span>
                        }
                    />
                    <Tab
                        label={
                            <span>
                                Cris Task <RequestStatus status={request.crisRequest.currentPlace} />
                            </span>
                        }
                    />
                </Tabs>

                <TabPanel index={0} value={this.state.tabValue}>
                    <Box display='flex'>
                        <Box flexGrow={1}>
                            <SingleEditText
                                header='Description'
                                value={request.description}
                                rows='4'
                                rowsMax='10'
                                onSave={(description) => {
                                    seamRequestService.save({ ...request, description }).subscribe((value) => {
                                        this.props.selectRequest(value);
                                    });
                                }}
                            />
                            <SeamRequestStats request={this.props.request} />
                            <Typography variant='h6' className='mt-2'>
                                Comments
                            </Typography>
                            <Comments
                                comments={this.props.seamComments}
                                addComment={(comment) => this.props.addSeamComment(comment, request).subscribe()}
                            />
                        </Box>
                        <Box flexGrow={0} flexBasis='15rem'>
                            <SeamSummary
                                request={request}
                                onChange={(field, value) => this.patchRequestProp(field, value)}
                            />
                        </Box>
                    </Box>
                </TabPanel>
                <TabPanel index={1} value={this.state.tabValue}>
                    <Box display='flex'>
                        <Box flexGrow={1}>
                            <CrisSummary request={request.crisRequest} />
                        </Box>
                        <Box flexGrow={0} component={Paper} className='p-2 ml-2' bgcolor='info.light'>
                            <Typography variant='h5'>CRIS Issue</Typography>
                            <RequestStatus status={request.crisRequest.currentPlace} />
                            <Typography variant='body1'>Due: {request.crisRequest.dueDate}</Typography>
                            <Typography variant='body1'>
                                Manager: {Formatters.userDisplayName(request.crisRequest.owner)}
                            </Typography>
                        </Box>
                    </Box>
                </TabPanel>
            </Box>
        );
    }

    private patchRequestProp(field: keyof SeamRequest, value: unknown) {
        seamRequestService.save({ ...this.props.request, [field]: value }).subscribe((updatedReq) => {
            this.props.selectRequest(updatedReq);
        });
    }

    private getActionButton() {
        let disabled = false;

        switch (this.props.request.currentPlace) {
            case SeamStatus.TUNE:
                if (undefined === this.props.patterns) {
                    return undefined;
                }

                disabled = 0 === this.props.patterns.length;

                return (
                    <RequireRole roles={[Role.TUNER, Role.SEAM_MANAGER]}>
                        <Badge
                            color='error'
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            badgeContent={disabled ? <Translate tkey='seam.request.noPatterns' /> : undefined}
                        >
                            <Button
                                type='button'
                                variant='contained'
                                color='primary'
                                disabled={disabled}
                                onClick={() => this.transition(SeamTransitions.READY_FOR_REVIEW)}
                            >
                                Send For Review
                            </Button>
                        </Badge>
                    </RequireRole>
                );
            case SeamStatus.REVIEW:
                return (
                    <RequireRole roles={Role.SEAM_MANAGER}>
                        <Button
                            type='button'
                            variant='contained'
                            color='primary'
                            className='ml-1'
                            onClick={() => this.transition(SeamTransitions.PATTERNS_APPROVED)}
                        >
                            Deploy Pre-Prod
                        </Button>
                        <Tooltip title='Sends back to patterns'>
                            <Button
                                type='button'
                                variant='contained'
                                color='secondary'
                                className='ml-1'
                                onClick={() => this.transition(SeamTransitions.PATTERNS_REJECT)}
                            >
                                Reject
                            </Button>
                        </Tooltip>
                    </RequireRole>
                );
            case SeamStatus.QA:
                return (
                    <>
                        <RequireRole roles={Role.QA_MANAGER}>{this.renderProdDeploy()}</RequireRole>
                        <RequireRole roles={Role.QA_REJECT}>
                            <Tooltip title='Sends back to patterns'>
                                <Button
                                    type='button'
                                    variant='contained'
                                    color='secondary'
                                    className='ml-1'
                                    onClick={() => this.transition(SeamTransitions.QA_REJECTED)}
                                >
                                    Reject
                                </Button>
                            </Tooltip>
                        </RequireRole>
                    </>
                );
            case SeamStatus.PROD_QA:
                return (
                    <RequireRole roles={Role.QA_MANAGER}>
                        <Button
                            onClick={this.handleClickProdQAApproved}
                            variant='contained'
                            color='primary'
                            className='ml-1'
                        >
                            Mark Approved
                        </Button>
                    </RequireRole>
                );
            default:
                return '';
        }
    }

    private renderProdDeploy(): React.ReactNode {
        if (this.props.patterns?.find((pattern: SeamPattern) => pattern.cobrander)) {
            return (
                <Tooltip title='There is a least 1 pattern with cobrander assigned. Not allowed to deploy'>
                    <Badge
                        color='error'
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        badgeContent='Cobrid Restricted'
                    >
                        <Button variant='outlined' color='inherit' className='ml-1' disabled>
                            Approve & Deploy Prod
                        </Button>
                    </Badge>
                </Tooltip>
            );
        }

        return (
            <Button onClick={this.handleClickPublish} variant='contained' color='primary' className='ml-1'>
                Approve & Deploy Prod
            </Button>
        );
    }

    private transition(transition: SeamTransitions) {
        seamRequestService.transition(this.props.request, transition).subscribe({
            next: (value) => {
                this.props.selectRequest(value);
            },
            error: (err: AjaxError) => {
                const resp = err.response as { message?: string };
                if (resp.message) {
                    messagingService.showError(resp.message);
                }
            },
        });
    }

    private getCloseButton() {
        switch (this.props.request.currentPlace) {
            case SeamStatus.PREPROD:
            case SeamStatus.PROD:
            case SeamStatus.CANCELLED:
                return undefined;
            case SeamStatus.CLOSED:
            case SeamStatus.PROD_QA:
                return (
                    <RequireRole roles={Role.QA_MANAGER}>
                        <Tooltip title='Reverts the changes done in Production'>
                            <Button onClick={this.handleClickOpen} variant='outlined' color='info' className='ml-1'>
                                Unpublish
                            </Button>
                        </Tooltip>
                    </RequireRole>
                );
            default:
                return (
                    <RequireRole roles={[Role.TUNER, Role.SEAM_MANAGER]}>
                        <Tooltip title='Closes the request'>
                            <Button
                                type='button'
                                variant='outlined'
                                color='secondary'
                                className='ml-1'
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'No further work will be possible once the request is closed.\n' +
                                                'Are you sure you want to close this request?',
                                        )
                                    ) {
                                        /* TODO material style modal*/
                                        this.transition(SeamTransitions.CLOSE_BY_USER);
                                    }
                                }}
                            >
                                Close
                            </Button>
                        </Tooltip>
                    </RequireRole>
                );
        }
    }
}

const mapStateToProps = (state: RootState) => ({
    seamComments: selectSeamComments(state),
    crisComments: selectCrisComments(state),
    roles: selectRoles(state),
});

const connected = connect(mapStateToProps, {
    loadSeamComments,
    loadCrisComments,
    setSeamComments: setSeamComments as ActionCreatorWithPayload<Comment[]>,
    setCrisComments: setCrisComments as ActionCreatorWithPayload<Comment[]>,
    addSeamComment,
    addCrisComment,
    selectRequest: selectRequest as ActionCreatorWithPayload<SeamRequest>,
})(Details);

export { connected as Details };
/* eslint max-lines: off */
